import jobServices from 'ApiService/Student/jobServices';
import InputField from 'components/fields/InputField';
import TextField from 'components/fields/TextField';
import { AuthContext } from 'contexts/AuthContext';
import ConfirmationModal from 'pages/Common/ConfirmationModal';
import { formatDate } from 'pages/helpers/dateUtils';
import { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { BallTriangle } from 'react-loader-spinner';

const Index = () => {
  const { state } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [showUnpauseModal, setShowUnpauseModal] = useState(false);
  const [isPlacementPaused, setIsPlacementPaused] = useState(false);
  const [pausesRemaining, setPausesRemaining] = useState(0);
  const [pauseInfo, setPauseInfo] = useState(null);
  const [pauseHistory, setPauseHistory] = useState([]);
  const [isPlacementPausedByAdmin, setIsPlacementPausedByAdmin] =
    useState(false);
  const [currentPauseRequestStatus, setCurrentPauseRequestStatus] =
    useState(null);
  const [currentUnpauseRequestStatus, setCurrentUnpauseRequestStatus] =
    useState(null);
  const [pauseDetails, setPauseDetails] = useState({
    startDate: '',
    endDate: '',
    reason: '',
  });
  const [unpauseReason, setUnpauseReason] = useState('');

  const [showPauseConfirmation, setShowPauseConfirmation] = useState(false);
  const [showUnpauseConfirmation, setShowUnpauseConfirmation] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchPauseStatus = async () => {
    try {
      setLoadingStatus(true);
      const response = await jobServices.getPauseStatus();
      if (response.data.success) {
        setIsPlacementPaused(response.data.isPlacementPaused);
        setPauseInfo(response.data.pauseInfo);
        setPausesRemaining(response.data.pausesRemaining ?? 0);
        setPauseHistory(response.data.pauseHistory);
        setIsPlacementPausedByAdmin(response.data.isPlacementPausedByAdmin);
        setCurrentPauseRequestStatus(response.data.currentPauseRequestStatus);
        setCurrentUnpauseRequestStatus(
          response.data.currentUnpauseRequestStatus
        );
      } else {
        toast.error('Failed to fetch placement pause status.');
      }
    } catch (error) {
      toast.error('Error fetching pause status.');
    } finally {
      setLoadingStatus(false);
    }
  };

  useEffect(() => {
    if (state?.user) {
      fetchPauseStatus();
    }
  }, [state?.user]);

  const handlePausePlacementClick = () => {
    if (
      !pauseDetails.startDate ||
      !pauseDetails.endDate ||
      !pauseDetails.reason
    ) {
      toast.error('Please fill in all fields.');
      return;
    }

    setShowPauseConfirmation(true);
  };

  const handlePausePlacement = async () => {
    try {
      setSubmitting(true);
      const response = await jobServices.pausePlacement(pauseDetails);
      if (response.data.success) {
        toast.success(response.data.message, { duration: 6000 });
        setShowPauseModal(false);
        setShowPauseConfirmation(false);
        fetchPauseStatus();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to request placement pause.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnpausePlacementClick = () => {
    setShowUnpauseModal(true);
  };

  const handleUnpauseConfirmation = () => {
    if (!unpauseReason) {
      toast.error('Please provide a reason for unpausing.');
      return;
    }
    setShowUnpauseConfirmation(true);
  };

  const handleUnpausePlacement = async () => {
    try {
      setSubmitting(true);
      const response = await jobServices.requestUnpausePlacement({
        unpauseReason,
      });
      if (response.data.success) {
        toast.success('Unpause placement request sent successfully to admin.');
        setShowUnpauseModal(false);
        setShowUnpauseConfirmation(false);
        fetchPauseStatus();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to request placement unpause.'
      );
      fetchPauseStatus();
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <BallTriangle color="#5431ed" />
      </div>
    );
  }

  const renderActionButton = () => {
    if (loadingStatus) {
      return <p>Loading...</p>;
    }

    if (isPlacementPausedByAdmin) {
      return (
        <div>
          <p className="text-sm text-red-800">
            Note: Placement is paused by the admin. Please contact your admin.
          </p>
        </div>
      );
    }

    if (currentPauseRequestStatus === 'pending') {
      return (
        <button
          disabled
          className="cursor-not-allowed rounded bg-gray-500 px-3 py-1 text-white"
        >
          Pause Request Pending Approval
        </button>
      );
    }

    if (currentUnpauseRequestStatus === 'pending') {
      return (
        <button
          disabled
          className="cursor-not-allowed rounded bg-gray-500 px-3 py-1 text-white"
        >
          Unpause Request Pending Approval
        </button>
      );
    }

    if (isPlacementPaused) {
      return (
        <button
          onClick={handleUnpausePlacementClick}
          className="rounded bg-blue-500 px-3 py-1 text-white hover:bg-blue-600"
        >
          Request to Unpause Placement
        </button>
      );
    }

    if (pausesRemaining <= 0) {
      return (
        <button
          disabled
          className="cursor-not-allowed rounded bg-gray-700 px-3 py-1 text-white"
        >
          Pause Placement (Limit Reached)
        </button>
      );
    }

    return (
      <button
        onClick={() => setShowPauseModal(true)}
        className="rounded bg-yellow-500 px-3 py-1 text-white hover:bg-yellow-600"
      >
        Request to Pause Placement ({pausesRemaining} left)
      </button>
    );
  };

  return (
    <div className="mt-10 space-y-6">
      {/* Status Display */}
      <div className="mb-6">
        <h3 className="mb-2 text-lg font-semibold dark:text-white">
          Placement Status
        </h3>
        <div className="rounded-lg bg-gray-100 p-4 dark:bg-navy-700">
          <p className="text-sm dark:text-white">
            {currentPauseRequestStatus === 'pending' && (
              <span className="mb-2 block text-yellow-600 dark:text-yellow-400">
                Your placement pause request is pending admin approval.
              </span>
            )}
            {currentUnpauseRequestStatus === 'pending' && (
              <span className="mb-2 block text-yellow-600 dark:text-yellow-400">
                Your placement unpause request is pending admin approval.
              </span>
            )}
            {isPlacementPaused && (
              <span className="mb-2 block text-red-600 dark:text-red-400">
                Your placement is currently paused.
              </span>
            )}
            {!isPlacementPaused && !currentPauseRequestStatus && (
              <span className="mb-2 block text-green-600 dark:text-green-400">
                Your placement is currently active.
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Action Button */}
      <div className="mb-6">{renderActionButton()}</div>

      {/* Pause History Table */}
      {pauseHistory.length > 0 && (
        <>
          <h3 className="text-lg font-semibold dark:text-white">
            Pause History:
          </h3>
          <div className="overflow-x-auto">
            <table
              className="w-full"
              variant="simple"
              color="gray-500"
              mb="24px"
            >
              <thead>
                <tr>
                  <th className="border-b border-gray-200 pb-2 pl-4 pr-4 text-center text-sm font-bold tracking-wide text-gray-600 dark:text-white lg:text-xs">
                    Sr No
                  </th>
                  <th className="border-b border-gray-200 pb-2 pl-4 pr-4 text-center text-sm font-bold tracking-wide text-gray-600 dark:text-white lg:text-xs">
                    Start Date
                  </th>
                  <th className="border-b border-gray-200 pb-2 pl-4 pr-4 text-center text-sm font-bold tracking-wide text-gray-600 dark:text-white lg:text-xs">
                    End Date
                  </th>
                  <th className="border-b border-gray-200 pb-2 pl-4 pr-4 text-center text-sm font-bold tracking-wide text-gray-600 dark:text-white lg:text-xs">
                    Reason
                  </th>
                  <th className="border-b border-gray-200 pb-2 pl-4 pr-4 text-center text-sm font-bold tracking-wide text-gray-600 dark:text-white lg:text-xs">
                    Unpause Reason
                  </th>
                </tr>
              </thead>
              <tbody>
                {pauseHistory.map((item, index) => (
                  <tr key={item._id} className="cursor-pointer">
                    <td className="pb-2 pl-4 pr-4 pt-2 text-center text-sm font-bold text-navy-700 dark:text-white">
                      {index + 1}
                    </td>
                    <td className="pb-2 pl-4 pr-4 pt-2 text-center text-sm font-bold text-navy-700 dark:text-white">
                      {formatDate(item.startDate)}
                    </td>
                    <td className="pb-2 pl-4 pr-4 pt-2 text-center text-sm font-bold text-navy-700 dark:text-white">
                      {formatDate(item.endDate)}
                    </td>
                    <td className="pb-2 pl-4 pr-4 pt-2 text-center text-sm font-bold text-navy-700 dark:text-white">
                      {item.reason}
                    </td>
                    <td className="pb-2 pl-4 pr-4 pt-2 text-center text-sm font-bold text-navy-700 dark:text-white">
                      {item.unpauseReason || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Pause Request Modal */}
      {showPauseModal && (
        <div className="bg-black fixed inset-0 z-50 flex items-center justify-center bg-opacity-50">
          <div className="relative w-full max-w-md rounded-lg bg-white p-6 shadow-lg dark:bg-navy-800">
            <button
              onClick={() => setShowPauseModal(false)}
              className="absolute right-2 top-2 text-2xl text-gray-500 hover:text-gray-700"
            >
              &times;
            </button>
            <h2 className="mb-4 text-lg font-semibold dark:text-white">
              Request to Pause Your Placement
            </h2>
            <div className="mb-4 text-sm text-gray-600 dark:text-gray-300">
              This will send a request to your admin to pause your placement.
              You'll be notified when they approve or reject it.
            </div>
            <InputField
              label="Start Date"
              type="date"
              value={pauseDetails.startDate}
              onChange={(e) =>
                setPauseDetails({ ...pauseDetails, startDate: e.target.value })
              }
            />
            <InputField
              label="End Date"
              type="date"
              value={pauseDetails.endDate}
              min={pauseDetails.startDate}
              onChange={(e) =>
                setPauseDetails({ ...pauseDetails, endDate: e.target.value })
              }
            />
            <TextField
              label="Reason"
              value={pauseDetails.reason}
              onChange={(e) =>
                setPauseDetails({ ...pauseDetails, reason: e.target.value })
              }
              placeholder="Please provide a detailed reason for pausing your placement"
            />
            <div className="mt-5 flex justify-end">
              <button
                onClick={handlePausePlacementClick}
                className="w-40 rounded-xl bg-brand-500 py-3 pl-2.5 pr-3 text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
              >
                Submit Request
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unpause Request Modal */}
      {showUnpauseModal && (
        <div className="bg-black fixed inset-0 z-50 flex items-center justify-center bg-opacity-50">
          <div className="relative w-full max-w-md rounded-lg bg-white p-6 shadow-lg dark:bg-navy-800">
            <button
              onClick={() => setShowUnpauseModal(false)}
              className="absolute right-2 top-2 text-2xl text-gray-500 hover:text-gray-700"
            >
              &times;
            </button>
            <h2 className="mb-4 text-lg font-semibold dark:text-white">
              Request to Unpause Your Placement
            </h2>
            <div className="mb-4 text-sm text-gray-600 dark:text-gray-300">
              This will send a request to your admin to unpause your placement.
              You'll be notified when they approve or reject it.
            </div>
            <TextField
              label="Reason for Unpausing"
              value={unpauseReason}
              onChange={(e) => setUnpauseReason(e.target.value)}
              placeholder="Please provide a reason for unpausing your placement"
            />
            <div className="mt-5 flex justify-end">
              <button
                onClick={handleUnpauseConfirmation}
                className="w-40 rounded-xl bg-brand-500 py-3 pl-2.5 pr-3 text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
              >
                Submit Request
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pause Request Confirmation Modal */}
      <ConfirmationModal
        isOpen={showPauseConfirmation}
        onClose={() => !submitting && setShowPauseConfirmation(false)}
        onConfirm={handlePausePlacement}
        title="Confirm Pause Placement Request"
        message={`Are you sure you want to request pausing your placement from ${formatDate(
          pauseDetails.startDate
        )} to ${formatDate(
          pauseDetails.endDate
        )}? Your request will need admin approval.`}
        confirmText={submitting ? 'Processing...' : 'Yes, Submit Request'}
        cancelText="Cancel"
        isSubmitting={submitting}
        icon="warning"
      />

      {/* Unpause Request Confirmation Modal */}
      <ConfirmationModal
        isOpen={showUnpauseConfirmation}
        onClose={() => !submitting && setShowUnpauseConfirmation(false)}
        onConfirm={handleUnpausePlacement}
        title="Confirm Unpause Placement Request"
        message="Are you sure you want to request unpausing your placement? Your request will need admin approval."
        confirmText={submitting ? 'Processing...' : 'Yes, Submit Request'}
        cancelText="Cancel"
        isSubmitting={submitting}
        icon="info"
      />
    </div>
  );
};

export default Index;
